import { useEffect, useMemo } from "react";
import { notification } from "@findemdev/maui";
import { useSearchCompanyListsQuery } from "../../../services/company";
import { ISearchFilter, ISearchSortFilter } from "../../../types/shared";
import { useGetCompanyLogosByMetaRefsQuery } from "../../../services/pub";
import { IGetCompanyLogosResponseDataObj } from "../../../types/pub";

interface IUsePopulateCompantLists {
  pageNumber: number;
  joid: string;
  filters?: ISearchFilter[];
  sortFields?: ISearchSortFilter;
  pageSize: number;
}

const usePopulateCompanyList = ({
  pageNumber,
  joid,
  filters,
  sortFields,
  pageSize = 10,
}: IUsePopulateCompantLists) => {

  const { data: allCompanyLists, isError: isCompanyListsError, isFetching: isCompanyListsFetching } = useSearchCompanyListsQuery({
    joid,
    page: pageNumber,
    size: pageSize,
    filters: filters,
    sort_fields: sortFields ? [sortFields] : undefined,
  }, {
    skip: !joid,
  });

  const { shortlistedCompanyMetaRefs, skipCompanyDetails } = useMemo(() => {
    const shortlistedCompanyMetaRefs = allCompanyLists?.data
      ?.map((value) => (value.shortlist?.map((company) => company.meta_ref).filter((metaRef) => metaRef !== undefined)))
      ?.filter((value) => value !== undefined)
      ?.flat();

    const skipCompanyDetails = !joid || isCompanyListsError || isCompanyListsFetching || !allCompanyLists || !shortlistedCompanyMetaRefs?.length;

    return {
      skipCompanyDetails,
      shortlistedCompanyMetaRefs,
    };
  }, [joid, isCompanyListsError, isCompanyListsFetching, allCompanyLists]);


  const { data: companyDetails, isError: isCompanyDetailsError, isFetching: isCompanyDetailsFetching } = useGetCompanyLogosByMetaRefsQuery({
    joid,
    metaRefs: shortlistedCompanyMetaRefs ?? [],
  }, {
    skip: skipCompanyDetails
  });

  const companyDetailsByMetaRef: Record<string, IGetCompanyLogosResponseDataObj | undefined> = useMemo(() => {
    if (!companyDetails) {
      return {};
    }

    const companyDetailsByMetaRef: Record<string, IGetCompanyLogosResponseDataObj | undefined> = companyDetails.data.reduce((acc, value) => {
      if (value?.company?.meta_ref) {
        const metaRef = value.company.meta_ref;
        acc[metaRef] = value;
      }

      return acc;
    }, {} as Record<string, IGetCompanyLogosResponseDataObj | undefined>);

    return companyDetailsByMetaRef;
  }, [companyDetails]);

  useEffect(() => {
    if (isCompanyListsError) {
      notification.error({
        message: 'Error',
        description: 'Error in fetching Company Lists'
      });
    }
  }, [isCompanyListsError]);

  useEffect(() => {
    if (isCompanyDetailsError) {
      notification.error({
        message: 'Error',
        description: 'Error in fetching Company Details',
      });
    }
  }, [isCompanyDetailsError]);

  return {
    allCompanyLists,
    isCompanyListsFetching,
    isCompanyListsError,
    companyDetails,
    isCompanyDetailsError,
    isCompanyDetailsFetching,
    companyDetailsByMetaRef,
    shortlistedCompanyMetaRefs,
  };
}

export default usePopulateCompanyList;